import { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

/**
 * Skeleton — loading shimmer (Anthropic warm tone, Sprint R8).
 * Real komponent shape'iga taqlid qiladi (text/avatar/card/row).
 *
 * Aurora violet glow YO'Q — faqat warm jigar gradient
 * (`--bg-surface` → `--bg-elevated`).
 *
 * Animation:
 *   - default `shimmer` — gradient sweep (1.8s ease-in-out)
 *   - `pulse` — opacity-only (kichik joylar uchun)
 *
 * Variants:
 *   <Skeleton variant="text" />
 *   <Skeleton variant="avatar" />
 *   <Skeleton variant="row" animation="pulse" />
 */
const skeletonVariants = cva("", {
  variants: {
    animation: {
      shimmer: "skeleton",
      pulse: "skeleton-pulse",
    },
    variant: {
      box: "",
      text: "h-4 rounded-md",
      title: "h-6 rounded-md",
      avatar: "h-9 w-9 rounded-full",
      "avatar-sm": "h-7 w-7 rounded-full",
      "avatar-rounded": "h-9 w-9 rounded-md",
      row: "h-10 w-full rounded-md",
      card: "h-32 w-full rounded-xl",
      button: "h-9 w-24 rounded-lg",
      badge: "h-5 w-16 rounded-full",
    },
  },
  defaultVariants: { variant: "box", animation: "shimmer" },
});

export interface SkeletonProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof skeletonVariants> {}

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(
  ({ className, variant, animation, ...props }, ref) => (
    <div
      ref={ref}
      role="status"
      aria-busy="true"
      aria-label="Yuklanmoqda"
      className={cn(skeletonVariants({ variant, animation }), className)}
      {...props}
    />
  ),
);
Skeleton.displayName = "Skeleton";

/**
 * Convenience: data table loading row (avatar + 3 column skeleton).
 * MembersTable, InvitationsTable yuklanish vaqtida ishlatish uchun.
 */
export function SkeletonTableRow({ columns = 4 }: { columns?: number }) {
  return (
    <div className="flex items-center gap-3 px-3 h-10 border-b border-[var(--border-subtle)]/50">
      <Skeleton variant="avatar-sm" />
      {Array.from({ length: columns - 1 }).map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          className={i === 0 ? "w-32" : "w-20"}
        />
      ))}
    </div>
  );
}
